import { matchesScriptPlatform, type ScriptPlatformFilter } from "../../../lib/script-platform.js";
import type { PersonaOpportunityStatusFilter } from "../../../types/persona-opportunities.types.js";

import type {
  HubChromeOpportunityRow,
  HubChromeScriptGroup,
  HubChromeScriptRow,
  OpportunityHubSelectionMix,
  OpportunityHubSetStatusFilter,
  OpportunityHubSetTypeFilter,
  OpportunityHubTabKey,
} from "./hub-chrome.types.js";

export const HUB_FILTER_ALL = "all";

export interface OpportunityHubFilters {
  setsSearch: string;
  setsStatus: OpportunityHubSetStatusFilter;
  setsType: OpportunityHubSetTypeFilter;
  opportunitiesSearch: string;
  opportunitySet: string;
  opportunityType: OpportunityHubSetTypeFilter;
  opportunityStatus: PersonaOpportunityStatusFilter;
  opportunityAction: string;
  opportunityFlow: string;
  opportunityFeature: string;
  opportunitySubFeature: string;
  opportunityEngine: string;
  scriptsSearch: string;
  scriptSet: string;
  scriptKind: "all" | "script" | "brief";
  scriptPlatform: ScriptPlatformFilter;
  scriptSent: "all" | "sent" | "not_sent";
  scriptFavorites: "all" | "favorites";
  sentSearch: string;
  sentStatus: "all" | "sent" | "in_production" | "delivered";
  sentVendor: string;
}

export const DEFAULT_OPPORTUNITY_HUB_FILTERS: OpportunityHubFilters = {
  setsSearch: "",
  setsStatus: "all",
  setsType: "all",
  opportunitiesSearch: "",
  opportunitySet: "all",
  opportunityType: "all",
  opportunityStatus: "all",
  opportunityAction: "all",
  opportunityFlow: "all",
  opportunityFeature: "all",
  opportunitySubFeature: "all",
  opportunityEngine: "all",
  scriptsSearch: "",
  scriptSet: "all",
  scriptKind: "all",
  scriptPlatform: "all",
  scriptSent: "all",
  scriptFavorites: "all",
  sentSearch: "",
  sentStatus: "all",
  sentVendor: "all",
};

export function applyHubDatasetChange(
  filters: OpportunityHubFilters,
  dataset: string,
): OpportunityHubFilters {
  return {
    ...filters,
    opportunityFlow: dataset,
    opportunityFeature: HUB_FILTER_ALL,
    opportunitySubFeature: HUB_FILTER_ALL,
  };
}

/** URL write for the five legacy facets. `dataset` and `flowName` stay equal. */
export function legacyFacetsToSearch(facets: {
  action: string;
  dataset: string;
  feature: string;
  subFeature: string;
  engine: string;
}): {
  action: string | undefined;
  dataset: string;
  flowName: string;
  feature: string | undefined;
  subFeature: string | undefined;
  engine: string | undefined;
} {
  const present = (value: string) => (value === HUB_FILTER_ALL ? undefined : value);
  return {
    action: present(facets.action),
    dataset: facets.dataset,
    flowName: facets.dataset,
    feature: present(facets.feature),
    subFeature: present(facets.subFeature),
    engine: present(facets.engine),
  };
}

/** A missing dataset stays unset so the first no-persona visit can preselect Angles. */
export function legacyFacetsFromSearch(search: {
  action?: string;
  dataset?: string;
  flowName?: string;
  feature?: string;
  subFeature?: string;
  engine?: string;
}): {
  action: string;
  dataset?: string;
  feature: string;
  subFeature: string;
  engine: string;
} {
  return {
    action: search.action ?? HUB_FILTER_ALL,
    dataset: search.dataset ?? search.flowName,
    feature: search.feature ?? HUB_FILTER_ALL,
    subFeature: search.subFeature ?? HUB_FILTER_ALL,
    engine: search.engine ?? HUB_FILTER_ALL,
  };
}

/** Which opportunity populations are on screen — filters follow the groups, not the set mix. */
export interface OpportunityFilterPresence {
  hasPersonaGroups: boolean;
  hasNoPersonaGroup: boolean;
  /** Sets the list covers — every set when none is selected. The set filter needs two or more. */
  setsInViewCount?: number;
}

/** Facet keys behind the Filters button — search lives in the toolbar row, not the panel. */
function facetKeys(
  tab: OpportunityHubTabKey,
  mix: OpportunityHubSelectionMix,
  presence?: OpportunityFilterPresence,
): (keyof OpportunityHubFilters)[] {
  if (tab === "sets") return ["setsStatus", "setsType"];
  if (tab === "opportunities") {
    const keys: (keyof OpportunityHubFilters)[] = [];
    if ((presence?.setsInViewCount ?? 0) > 1) keys.push("opportunitySet");
    if (presence?.hasPersonaGroups) keys.push("opportunityStatus");
    if (presence?.hasNoPersonaGroup) {
      keys.push(
        "opportunityAction",
        "opportunityFlow",
        "opportunityFeature",
        "opportunitySubFeature",
        "opportunityEngine",
      );
    }
    return keys;
  }
  if (tab === "scripts") {
    const keys: (keyof OpportunityHubFilters)[] = [];
    if ((presence?.setsInViewCount ?? 0) > 1) keys.push("scriptSet");
    keys.push("scriptKind", "scriptPlatform", "scriptSent", "scriptFavorites");
    return keys;
  }
  return [];
}

export function countActiveHubFilters(
  tab: OpportunityHubTabKey,
  filters: OpportunityHubFilters,
  mix: OpportunityHubSelectionMix,
  presence?: OpportunityFilterPresence,
): number {
  return facetKeys(tab, mix, presence).filter((key) => filters[key] !== HUB_FILTER_ALL).length;
}

/**
 * Opportunities-tab row predicate. Status is an exact match on every row.
 * The synthetic "Your Scripts" bucket is not an angle, so it never appears here.
 * Action, flow, and feature stay competitor-only — they do not hide persona rows.
 */
export function filterHubOpportunityRows(
  rows: readonly HubChromeOpportunityRow[],
  filters: OpportunityHubFilters,
): HubChromeOpportunityRow[] {
  const query = filters.opportunitiesSearch.trim().toLowerCase();
  return rows.filter((row) => {
    if (row.isCustom) return false;
    const isCompetitor = row.setType === "COMPETITOR";
    if (filters.opportunitySet !== "all" && row.setId !== filters.opportunitySet) return false;
    if (filters.opportunityType !== "all" && row.setType !== filters.opportunityType) return false;
    if (filters.opportunityStatus !== "all" && row.status !== filters.opportunityStatus) return false;
    if (filters.opportunityAction !== "all" && isCompetitor && row.action !== filters.opportunityAction) {
      return false;
    }
    if (filters.opportunityFlow !== "all" && isCompetitor && row.dataset !== filters.opportunityFlow) {
      return false;
    }
    if (
      filters.opportunityFeature !== "all" &&
      isCompetitor &&
      row.feature !== filters.opportunityFeature
    ) {
      return false;
    }
    if (
      filters.opportunitySubFeature !== "all" &&
      isCompetitor &&
      row.subFeature !== filters.opportunitySubFeature
    ) {
      return false;
    }
    if (
      filters.opportunityEngine !== "all" &&
      isCompetitor &&
      row.engine !== filters.opportunityEngine
    ) {
      return false;
    }
    if (!query) return true;
    if (isCompetitor) {
      return (
        (row.topicTitle?.toLowerCase().includes(query) ?? false) ||
        (row.topicDescription?.toLowerCase().includes(query) ?? false) ||
        (row.dataset?.toLowerCase().includes(query) ?? false) ||
        (row.suggestion?.toLowerCase().includes(query) ?? false)
      );
    }
    return (
      row.angle.toLowerCase().includes(query) ||
      (row.personaName?.toLowerCase().includes(query) ?? false)
    );
  });
}

/** A status pick unfolds every remaining group so the matching opportunities stay visible. */
export function expandedHubOpportunityGroupKeys(
  status: PersonaOpportunityStatusFilter,
  groupKeys: readonly string[],
  userExpandedKeys: readonly string[],
): Set<string> {
  if (status !== "all") return new Set(groupKeys);
  return new Set(userExpandedKeys);
}

/**
 * Set rows, not the opportunity drilldown. An empty drilled opportunity still
 * has scripts when another opportunity on the same set does.
 */
export function selectedSetsHaveNoDocuments(
  sets: readonly { scriptCount: number; briefCount: number }[],
): boolean {
  // every([]) is true, so an unresolved selection must not count as an empty set.
  return sets.length > 0 && sets.every((set) => set.scriptCount + set.briefCount === 0);
}

export function selectedSetsHaveNoOpportunities(
  sets: readonly { opportunityCount: number }[],
): boolean {
  return sets.length > 0 && sets.every((set) => set.opportunityCount === 0);
}

/** Empty-set footer copy applies only when the user picked set(s), not the implicit all-sets view. */
export function hubShowsEmptySetFooter(
  hasExplicitSetSelection: boolean,
  sets: readonly { opportunityCount: number }[],
): boolean {
  if (!hasExplicitSetSelection) return false;
  return selectedSetsHaveNoOpportunities(sets);
}

export function hubShowsEmptyDocumentsFooter(
  hasExplicitSetSelection: boolean,
  sets: readonly { scriptCount: number; briefCount: number }[],
): boolean {
  if (!hasExplicitSetSelection || sets.length === 0) return false;
  return selectedSetsHaveNoDocuments(sets);
}

/** Scripts-tab row predicate. Favorites is a display filter on the starred rows. */
export function filterHubScriptRows(
  rows: readonly HubChromeScriptRow[],
  filters: OpportunityHubFilters,
): HubChromeScriptRow[] {
  const query = filters.scriptsSearch.trim().toLowerCase();
  return rows.filter((row) => {
    if (filters.scriptKind !== "all" && row.kind !== filters.scriptKind) return false;
    if (!matchesScriptPlatform(row.youtubeFormat, filters.scriptPlatform)) return false;
    if (filters.scriptSent !== "all" && row.sent !== (filters.scriptSent === "sent")) return false;
    if (filters.scriptFavorites === "favorites" && !row.isFavorite) return false;
    return !query || row.title.toLowerCase().includes(query);
  });
}

/**
 * The set and kind filters hide groups by their server fields, so they also apply
 * to collapsed groups. The other lenses can only narrow rows that have loaded.
 */
export function filterHubScriptGroups(
  groups: readonly HubChromeScriptGroup[],
  filters: OpportunityHubFilters,
): HubChromeScriptGroup[] {
  return groups
    .filter((group) => {
      if (filters.scriptSet !== HUB_FILTER_ALL && group.setId !== filters.scriptSet) return false;
      if (filters.scriptKind === "script") return group.scriptCount > 0;
      if (filters.scriptKind === "brief") return group.briefCount > 0;
      return true;
    })
    .map((group) => ({
      ...group,
      scriptCount: filters.scriptKind === "brief" ? 0 : group.scriptCount,
      briefCount: filters.scriptKind === "script" ? 0 : group.briefCount,
      rows: filterHubScriptRows(group.rows, filters),
    }));
}

export function clearHubFilters(
  tab: OpportunityHubTabKey,
  filters: OpportunityHubFilters,
  mix: OpportunityHubSelectionMix,
  presence?: OpportunityFilterPresence,
): OpportunityHubFilters {
  const next = { ...filters };
  for (const key of facetKeys(tab, mix, presence)) {
    next[key] = HUB_FILTER_ALL as never;
  }
  return next;
}
